import { getApiErrorDetails, hasApiErrorCode } from '@/utils/apiError'
import type { ScheduleShiftsDto } from '@/Network/Types/Requests/DsAttendanceRequest'
import type { DSAttendanceContainerDetailResponse } from '@/Network/Types/Responses/DsAttendancePolicyResponses'
import { newSheetActionFor } from '@/modules/company/Approvals/DsAttendance/utils/new-sheet'

/**
 * #3072 — o upload não acha registro aberto porque o da data foi REPROVADO e não tem
 * substituto. Quem reenvia é o time de Escalas, e a folha corrigida entra como NOVA FOLHA do
 * mesmo registro (`POST containers/:id/new-sheet`). O backend manda o id em `details`.
 */
export const REJECTED_WITHOUT_REPLACEMENT_CODE =
  'DS_ATTENDANCE_CONTAINER_REJECTED_WITHOUT_REPLACEMENT'

/** Espelha o mínimo do DTO do backend, como o `DsAttendanceNewSheetModal`. */
export const NEW_SHEET_MIN_JUSTIFICATION = 10

/** Id do registro reprovado que o erro do upload aponta, ou `null` (outra causa, excluído). */
export function rejectedContainerIdFrom(error: unknown): number | null {
  if (!hasApiErrorCode(error, REJECTED_WITHOUT_REPLACEMENT_CODE)) return null
  const details = getApiErrorDetails(error)
  if (details?.status !== 'REJECTED') return null
  const id = Number(details.containerId)
  return Number.isInteger(id) && id > 0 ? id : null
}

type RecordForNewSheet = Pick<
  DSAttendanceContainerDetailResponse,
  'status' | 'monitoringType' | 'replacementContainerId' | 'schedule' | 'shifts'
> & { kind?: string | null }

/**
 * A nova folha só substitui a folha DESTE registro, mas a folha física costuma cobrir várias
 * escalas (no caso da #3072, a UTI manda a mesma folha para duas escalas). Separa o envio: a
 * escala do registro reprovado sai da seleção (vai como nova folha dele) e o resto (`remaining`)
 * segue pelo upload normal.
 *
 * `null` quando a nova folha não serve: registro que não aceita nova folha (substituto ativo,
 * atividade, não diária…), seleção sem a escala do registro, ou turno selecionado dessa escala
 * que o registro não cobre — aí a folha também seria de outro registro da mesma escala.
 */
export function splitSelectionForNewSheet(
  record: RecordForNewSheet | null,
  selection: ScheduleShiftsDto[]
): { remaining: ScheduleShiftsDto[] } | null {
  if (newSheetActionFor(record, true) !== 'SUBMIT' || !record) return null

  const recordScheduleId = Number(record.schedule?.id)
  const recordEntry = selection.find((entry) => Number(entry.scheduleId) === recordScheduleId)
  if (!recordEntry || recordEntry.shiftIds.length === 0) return null

  const recordShiftIds = new Set((record.shifts ?? []).map((shift) => Number(shift.id)))
  if (!recordEntry.shiftIds.every((id) => recordShiftIds.has(Number(id)))) return null

  return { remaining: selection.filter((entry) => entry !== recordEntry) }
}
